import type { WishlistModelTransformed } from '~/core/entities/api/ApiWishlist';

import { useFavoritesCookie } from '~/composables/useFavoritesCookie';
import { extractErrorLogContext, useLogger } from '~/composables/useLogger';
import { useWishlistStore } from '~/composables/useWishlistStore';
import { useTrackingIdentity } from '~/composables/visitorIdentity';
import { nuxtWishlistRepository } from '~~/adapters/repositories/nuxt/nuxtWishlistRepository';

export interface WishlistsState {
	currentWishlistId: string;
	propertyCount: number;
	wishlists: WishlistModelTransformed[];
	favoritePropertyIds: string[];
}

function getWishlistKey(propertyId: string): string {
	const { user } = useUserSession();
	const userId = user.value?.id || 'anonymous';
	return `${userId}:${propertyId}`;
}

/**
 * State for tracking in-flight wishlist operations
 * Using arrays instead of Set/Map for Vue reactivity compatibility
 */
interface WishlistOperationsState {
	inFlightOps: string[];
	pendingTargets: Array<{ key: string; target: boolean }>;
}

/**
 * Main wishlist composable that provides wishlist functionality and API operations
 * This composable manages wishlist operations like adding/removing properties from wishlists
 */
export async function useWishlists() {
	const logger = useLogger('Wishlists');
	// Use arrays instead of Set/Map for Vue reactivity
	const operationsState = useState<WishlistOperationsState>(
		'useWishlists:operationsState',
		() => ({
			inFlightOps: [],
			pendingTargets: []
		})
	);

	// Helper functions for operations state management
	function hasInFlightOp(key: string): boolean {
		return operationsState.value.inFlightOps.includes(key);
	}

	function addInFlightOp(key: string): void {
		if (!hasInFlightOp(key)) {
			operationsState.value.inFlightOps.push(key);
		}
	}

	function removeInFlightOp(key: string): void {
		const index = operationsState.value.inFlightOps.indexOf(key);
		if (index > -1) {
			operationsState.value.inFlightOps.splice(index, 1);
		}
	}

	function getPendingTarget(key: string): boolean | undefined {
		const entry = operationsState.value.pendingTargets.find(p => p.key === key);
		return entry?.target;
	}

	function setPendingTarget(key: string, target: boolean): void {
		const existingIndex = operationsState.value.pendingTargets.findIndex(
			p => p.key === key
		);
		if (existingIndex > -1) {
			const existing = operationsState.value.pendingTargets[existingIndex];
			if (existing) {
				existing.target = target;
			}
		} else {
			operationsState.value.pendingTargets.push({ key, target });
		}
	}

	function removePendingTarget(key: string): void {
		const index = operationsState.value.pendingTargets.findIndex(
			p => p.key === key
		);
		if (index > -1) {
			operationsState.value.pendingTargets.splice(index, 1);
		}
	}

	function hasPendingTarget(key: string): boolean {
		return operationsState.value.pendingTargets.some(p => p.key === key);
	}

	const store = await useWishlistStore();

	const { refresh: refreshFavoritesCookie } = useFavoritesCookie();
	const getTrackingIdentity = useTrackingIdentity();

	const {
		clearWishlistsOnLogout,
		registerInitialFavoriteState,
		isPropertyFavorited,
		setCurrentWishlistId,
		executeFetch,
		isFetching
	} = store;
	// Get wishlistsState from store
	const { wishlistsState } = store;

	// Create a reactive reference to the state
	const state = computed((): WishlistsState => wishlistsState.value);

	const currentWishlistId = computed(() => {
		return state.value.currentWishlistId;
	});

	async function addToWishlist(propertyId: string, wishlistId?: string) {
		const [result, error] = await nuxtWishlistRepository.addProperty({
			propertyId,
			wishlistId,
			...getTrackingIdentity()
		});

		if (error) {
			logger.error('[Wishlist Error] Failed to add property to wishlist', {
				propertyId,
				wishlistId,
				...extractErrorLogContext(error)
			});
			return [null, error] as const;
		}

		// Update cookie to trigger automatic refresh via useFetch watch
		const { user } = useUserSession();
		refreshFavoritesCookie(user.value?.id);

		return [result, null] as const;
	}

	async function removeFromWishlist(propertyId: string, wishlistId?: string) {
		const [result, error] = await nuxtWishlistRepository.removeProperty({
			propertyId,
			wishlistId
		});

		if (error) {
			logger.error('[Wishlist Error] Failed to remove property from wishlist', {
				propertyId,
				wishlistId,
				...extractErrorLogContext(error)
			});
			return [null, error] as const;
		}

		// Update cookie to trigger automatic refresh via useFetch watch
		const { user } = useUserSession();
		refreshFavoritesCookie(user.value?.id);

		return [result, null] as const;
	}

	async function executeWishlistOperation(
		key: string,
		propertyId: string,
		target: boolean,
		wishlistId?: string
	) {
		addInFlightOp(key);

		try {
			if (target) {
				return await addToWishlist(propertyId, wishlistId);
			} else {
				return await removeFromWishlist(propertyId, wishlistId);
			}
		} finally {
			removeInFlightOp(key);
		}
	}

	async function toggleWishlist(
		propertyId: string,
		isFavorite: boolean,
		wishlistId?: string
	) {
		const key = getWishlistKey(propertyId);
		const desiredTarget = !isFavorite;

		setPendingTarget(key, desiredTarget);

		if (hasInFlightOp(key)) {
			return [null, null] as const;
		}

		let lastResult = null;
		let lastError = null;

		while (hasPendingTarget(key)) {
			const target = getPendingTarget(key);
			if (target === undefined) break;

			removePendingTarget(key);

			const [result, error] = await executeWishlistOperation(
				key,
				propertyId,
				target,
				wishlistId
			);
			lastResult = result;
			lastError = error;
		}

		return [lastResult, lastError] as const;
	}

	/**
	 * Updates local wishlist state for optimistic UI updates
	 * @param propertyId - ID of the property to update
	 * @param isFavorite - Whether the property should be favorited
	 */
	function updateLocalWishlist(propertyId: string, isFavorite: boolean): void {
		if (!propertyId || !state.value || !state.value.favoritePropertyIds) return;

		if (isFavorite) {
			if (!state.value.favoritePropertyIds.includes(propertyId)) {
				state.value.favoritePropertyIds.push(propertyId);
			}
		} else {
			const index = state.value.favoritePropertyIds.indexOf(propertyId);
			if (index > -1) {
				state.value.favoritePropertyIds.splice(index, 1);
			}
		}
		state.value.propertyCount = state.value.favoritePropertyIds.length;
	}

	return {
		wishlistsState,

		// State management actions
		setCurrentWishlistId,
		currentWishlistId,
		registerInitialFavoriteState,
		clearWishlistsOnLogout,

		// API actions
		addToWishlist,
		removeFromWishlist,
		toggleWishlist,

		// Utility functions
		updateLocalWishlist,
		isPropertyFavorited,
		isFetching,
		executeFetch
	};
}
